export type KnowledgeVisibility = 'internal' | 'customer_safe' | 'public';

export type KnowledgeProcessingStatus =
  | 'uploaded'
  | 'parsing'
  | 'chunking'
  | 'embedding'
  | 'ready'
  | 'failed';


export const isStructuredKnowledgeDocument = (document: KnowledgeDocument): boolean =>
  Boolean(document.fileCategory?.trim());

export interface KnowledgeDocument {
  id: string;
  title: string;
  originalFileName: string;
  storedFileName: string;
  mimeType: string;
  fileSize: number;
  mediaUrl: string;
  publicUrl: string;
  visibility: KnowledgeVisibility;
  processingStatus: KnowledgeProcessingStatus;
  tags: string[];
  summary?: string | null;
  fileCategory?: string | null;
  attachmentCount: number;
  createdByEmployeeId: string;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeAttachment {
  id: string;
  knowledgeDocumentId: string;
  role: string;
  sortOrder: number;
  fileName: string;
  mimeType: string;
  fileSize: number;
  mediaUrl: string;
  accessibleUrl: string;
  caption: string;
  altText: string;
  createdByEmployeeId: string;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeAttachmentSettings {
  enabled: boolean;
  maxAttachmentsPerDocument: number;
  maxAttachmentBytes: number;
  maxCaptionLength: number;
  maxAltTextLength: number;
  allowedImageMimeTypesCsv: string;
}

export interface UploadKnowledgeAttachmentRequest {
  file: File;
  caption?: string;
  altText?: string;
  role?: string;
  sortOrder?: number;
}

export interface UpdateKnowledgeAttachmentRequest {
  caption?: string;
  altText?: string;
  role?: string;
  sortOrder?: number;
}

export const KNOWLEDGE_UNCATEGORIZED_FILE_CATEGORY = '__none__';

export type KnowledgeDocumentFileCategoryFilter = 'all' | typeof KNOWLEDGE_UNCATEGORIZED_FILE_CATEGORY | string;

export interface KnowledgeDocumentListQuery {
  page?: number;
  pageSize?: number;
  visibility?: KnowledgeVisibility | 'all';
  status?: KnowledgeProcessingStatus | 'all';
  search?: string;
  fileCategory?: KnowledgeDocumentFileCategoryFilter;
}

export interface PagedKnowledgeDocumentResult {
  page: number;
  pageSize: number;
  totalCount: number;
  items: KnowledgeDocument[];
}

export interface UploadKnowledgeDocumentRequest {
  file: File;
  title?: string;
  visibility?: KnowledgeVisibility;
  tags?: string;
  summary?: string;
  fileCategory?: string;
}

export type KnowledgeDataSourceType = 'document' | 'consultation' | 'csv' | 'google_sheets';

export type KnowledgeExternalSyncAuthType = 'none' | 'bearer' | 'api_key_header';

export type KnowledgeGoogleSheetsAuthType = 'public' | 'service_account' | 'oauth';

export interface KnowledgeGoogleSheetsSource {
  spreadsheetId: string;
  sheetGid: string;
  syncIntervalMinutes: number;
  lastSyncedAt?: string;
  authType: KnowledgeGoogleSheetsAuthType;
  authConfigured: boolean;
}

export interface KnowledgeConsultationSource {
  templateId: string;
  fieldDefinitionId: string;
  flattenRepeater: boolean;
  syncIntervalMinutes: number;
  lastSyncedAt?: string;
  includeStatuses: string[];
  includeMetadataFields: string[];
  additionalFieldDefinitionIds: string[];
  autoSyncOnConsultationChange: boolean;
  autoSyncOnFieldValueChange: boolean;
}

export interface KnowledgeDataCategory {
  categoryId: string;
  displayName: string;
  description: string;
  dataRootPath: string;
  dataSourceType: KnowledgeDataSourceType;
  externalSyncUrl: string;
  externalSyncTitle: string;
  externalSyncIntervalMinutes: number;
  externalSyncLastSyncedAt?: string;
  externalSyncAuthType: KnowledgeExternalSyncAuthType;
  externalSyncAuthHeaderName: string;
  externalSyncAuthConfigured: boolean;
  consultationSource?: KnowledgeConsultationSource;
  googleSheetsSource?: KnowledgeGoogleSheetsSource;
}

export interface KnowledgeDataCategoryOverview extends KnowledgeDataCategory {
  toolCount: number;
  documentCount: number;
  readyDocumentCount: number;
  latestDocumentTitle?: string;
  latestDocumentUpdatedAt?: string;
}

export interface CreateKnowledgeDataCategoryRequest {
  categoryId: string;
  displayName: string;
  description?: string;
  dataRootPath?: string;
  dataSourceType?: KnowledgeDataSourceType;
  externalSyncUrl?: string;
  externalSyncTitle?: string;
  externalSyncIntervalMinutes?: number;
  externalSyncAuthType?: KnowledgeExternalSyncAuthType;
  externalSyncAuthHeaderName?: string;
  externalSyncAuthValue?: string;
  clearExternalSyncAuthValue?: boolean;
  consultationTemplateId?: string;
  consultationFieldDefinitionId?: string;
  consultationFlattenRepeater?: boolean;
  consultationSyncIntervalMinutes?: number;
  consultationIncludeStatuses?: string[];
  consultationIncludeMetadataFields?: string[];
  consultationAdditionalFieldDefinitionIds?: string[];
  consultationAutoSyncOnConsultationChange?: boolean;
  consultationAutoSyncOnFieldValueChange?: boolean;
  googleSheetsSpreadsheetId?: string;
  googleSheetsSheetGid?: string;
  googleSheetsSyncIntervalMinutes?: number;
  googleSheetsAuthType?: KnowledgeGoogleSheetsAuthType;
  googleSheetsAuthValue?: string;
}

export interface UpdateKnowledgeDataCategoryRequest {
  displayName: string;
  description?: string;
  dataRootPath?: string;
  dataSourceType?: KnowledgeDataSourceType;
  externalSyncUrl?: string;
  externalSyncTitle?: string;
  externalSyncIntervalMinutes?: number;
  externalSyncAuthType?: KnowledgeExternalSyncAuthType;
  externalSyncAuthHeaderName?: string;
  externalSyncAuthValue?: string;
  clearExternalSyncAuthValue?: boolean;
  consultationTemplateId?: string;
  consultationFieldDefinitionId?: string;
  consultationFlattenRepeater?: boolean;
  consultationSyncIntervalMinutes?: number;
  consultationIncludeStatuses?: string[];
  consultationIncludeMetadataFields?: string[];
  consultationAdditionalFieldDefinitionIds?: string[];
  consultationAutoSyncOnConsultationChange?: boolean;
  consultationAutoSyncOnFieldValueChange?: boolean;
  googleSheetsSpreadsheetId?: string;
  googleSheetsSheetGid?: string;
  googleSheetsSyncIntervalMinutes?: number;
  googleSheetsAuthType?: KnowledgeGoogleSheetsAuthType;
  googleSheetsAuthValue?: string;
  clearGoogleSheetsAuthValue?: boolean;
}

export interface GoogleSheetsOAuthStartResponse {
  authorizeUrl: string;
}

export interface KnowledgeGoogleSheetsSettingsResponse {
  oauthClientId: string;
  oauthClientSecretConfigured: boolean;
  oauthRedirectUri: string;
  oauthFrontendReturnUrl: string;
  oauthFrontendReturnUrlResolved: string;
  oauthAppConfigured: boolean;
  oauthAppManagedByPlatform: boolean;
  googleAccountConnected: boolean;
}

export interface UpdateKnowledgeGoogleSheetsSettingsRequest {
  oauthClientId?: string;
  oauthClientSecret?: string;
  oauthRedirectUri?: string;
  oauthFrontendReturnUrl?: string;
  clearOAuthClientSecret?: boolean;
}

export interface KnowledgeCategoryJsonSyncResult {
  documentId: string;
  title: string;
  updatedAt: string;
}

export type KnowledgeDynamicToolMatchMode =
  | 'contains'
  | 'equals'
  | 'startswith'
  | 'endswith'
  | 'numericequals'
  | 'numericgte'
  | 'numericlte'
  | 'dateequals'
  | 'datebefore'
  | 'dateafter'
  | 'in'
  | 'exists'
  | 'isempty';

export type KnowledgeDynamicToolParameterFormat = 'text' | 'date' | 'email' | 'id' | 'number' | '';

export type KnowledgeDynamicToolFilterLogic = 'and' | 'or';

export type KnowledgeDynamicToolResultMode = 'list' | 'count' | 'groupby';

export interface KnowledgeDynamicToolParameter {
  name: string;
  description: string;
  jsonPath: string;
  matchMode: KnowledgeDynamicToolMatchMode | string;
  isRequired: boolean;
  allowedValues?: string[];
  example?: string;
  format?: KnowledgeDynamicToolParameterFormat | string;
  defaultValue?: string;
}

export interface KnowledgeDynamicToolResponseField {
  name: string;
  jsonPath: string;
}

export interface KnowledgeDynamicTool {
  toolId: string;
  categoryId: string;
  kernelFunctionName: string;
  displayName: string;
  description: string;
  dataRootPath: string;
  parameters: KnowledgeDynamicToolParameter[];
  responseFields: KnowledgeDynamicToolResponseField[];
  responseInstruction: string;
  maxResults: number;
  sortJsonPath: string;
  sortDirection: 'asc' | 'desc' | string;
  filterLogic: KnowledgeDynamicToolFilterLogic | string;
  resultMode: KnowledgeDynamicToolResultMode | string;
  groupByJsonPath: string;
  isPublished: boolean;
  knowledgeSearchParameter: string;
  knowledgeSearchMaxChunks: number;
  functionId: string;
}

export interface CreateKnowledgeDynamicToolRequest {
  categoryId?: string;
  kernelFunctionName: string;
  displayName: string;
  description: string;
  dataRootPath?: string;
  parameters: KnowledgeDynamicToolParameter[];
  responseFields?: KnowledgeDynamicToolResponseField[];
  responseInstruction?: string;
  maxResults?: number;
  sortJsonPath?: string;
  sortDirection?: 'asc' | 'desc' | string;
  filterLogic?: KnowledgeDynamicToolFilterLogic | string;
  resultMode?: KnowledgeDynamicToolResultMode | string;
  groupByJsonPath?: string;
  isPublished?: boolean;
  knowledgeSearchParameter?: string;
  knowledgeSearchMaxChunks?: number;
  enableForReplyUseCase?: boolean;
  assignedUseCaseIds?: string[];
}

export interface UpdateKnowledgeDynamicToolRequest {
  kernelFunctionName: string;
  displayName: string;
  description: string;
  dataRootPath?: string;
  parameters: KnowledgeDynamicToolParameter[];
  responseFields?: KnowledgeDynamicToolResponseField[];
  responseInstruction?: string;
  maxResults?: number;
  sortJsonPath?: string;
  sortDirection?: 'asc' | 'desc' | string;
  filterLogic?: KnowledgeDynamicToolFilterLogic | string;
  resultMode?: KnowledgeDynamicToolResultMode | string;
  groupByJsonPath?: string;
  isPublished?: boolean;
  knowledgeSearchParameter?: string;
  knowledgeSearchMaxChunks?: number;
  enableForReplyUseCase?: boolean;
  assignedUseCaseIds?: string[];
}

export interface PreviewKnowledgeDynamicToolDraft {
  categoryId: string;
  dataRootPath?: string;
  parameters: KnowledgeDynamicToolParameter[];
  responseFields?: KnowledgeDynamicToolResponseField[];
  responseInstruction?: string;
  maxResults?: number;
  sortJsonPath?: string;
  sortDirection?: 'asc' | 'desc' | string;
  filterLogic?: KnowledgeDynamicToolFilterLogic | string;
  resultMode?: KnowledgeDynamicToolResultMode | string;
  groupByJsonPath?: string;
  knowledgeSearchParameter?: string;
  knowledgeSearchMaxChunks?: number;
}

export interface PreviewKnowledgeDynamicToolRequest {
  toolId?: string;
  tool?: PreviewKnowledgeDynamicToolDraft;
  arguments?: Record<string, string | null | undefined>;
}

export interface PreviewKnowledgeDynamicToolResult {
  categoryId?: string;
  documentId?: string;
  documentTitle?: string;
  documentUpdatedAt?: string;
  matchedCount?: number;
  count?: number;
  truncated?: boolean;
  maxResults?: number;
  appliedFilters?: Record<string, string | null>;
  filterLogic?: string;
  resultMode?: string;
  groupByJsonPath?: string;
  groups?: unknown[];
  relatedKnowledge?: unknown[];
  llmInstruction?: string;
  error?: string;
  items?: unknown[];
  isSynthetic?: boolean;
}

export interface JsonSchemaField {
  jsonPath: string;
  name: string;
  type: string;
  sampleValues: string[];
}

export interface JsonSchemaIntrospectionResult {
  categoryId: string;
  dataRootPath: string;
  fields: JsonSchemaField[];
  isSynthetic?: boolean;
}

export interface IntrospectKnowledgeJsonSchemaRequest {
  documentId?: string;
  categoryId?: string;
  dataRootPath?: string;
}

export interface UpdateKnowledgeDocumentTitleRequest {
  title: string;
}

export interface UpdateKnowledgeDocumentSummaryRequest {
  summary: string;
}

export interface UpdateKnowledgeDocumentTagsRequest {
  tags: string;
}

export interface ReprocessKnowledgeDocumentRequest {
  force?: boolean;
}

export interface KnowledgeIngestionJob {
  id: string;
  knowledgeDocumentId: string;
  status: string;
  attempts: number;
  error?: string | null;
  createdAt: string;
  updatedAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
}

export interface KnowledgeRetrieveRequest {
  query: string;
  maxChunks?: number;
  audience?: 'employee' | 'customer';
}

export interface KnowledgeRetrievedChunk {
  chunkId: string;
  documentId: string;
  documentName: string;
  text: string;
  visibility: string;
  chunkIndex: number;
}

export interface KnowledgeRetrieveResponse {
  query: string;
  chunks: KnowledgeRetrievedChunk[];
}

export type KnowledgeRetrievalContextType = 'ai_reply' | 'ai_draft' | 'ai_summary';

export interface KnowledgeRetrievalLog {
  id: string;
  conversationId?: string | null;
  conversationTitle?: string | null;
  contextType?: string | null;
  query?: string | null;
  retrievalMethod?: string | null;
  retrievedChunkIds?: string | null;
  retrievedChunkCount: number;
  averageRelevanceScore?: number | null;
  latencyMilliseconds: number;
  embeddingModel?: string | null;
  userFoundHelpful?: boolean | null;
  feedback?: string | null;
  createdAt: string;
}

export interface KnowledgeRetrievalLogListQuery {
  page?: number;
  pageSize?: number;
  conversationId?: string;
  contextType?: KnowledgeRetrievalContextType | 'all';
}

export interface PagedKnowledgeRetrievalLogResult {
  page: number;
  pageSize: number;
  totalCount: number;
  items: KnowledgeRetrievalLog[];
}

export interface SubmitKnowledgeRetrievalFeedbackRequest {
  helpful: boolean;
  feedback?: string;
}

export interface KnowledgeBaseRetrievalSettings {
  semanticScoreThreshold: number;
  defaultMaxChunks: number;
  maxAllowedChunks: number;
  queryMessageCount: number;
  embeddingQueryMaxChars: number;
  keywordQueryMaxChars: number;
}

export interface KnowledgeBaseIngestionSettings {
  chunkMaxChars: number;
  chunkOverlapChars: number;
  pollIntervalSeconds: number;
  retryMaxAttempts: number;
  retryBackoffBaseSeconds: number;
  retryBackoffMaxSeconds: number;
  stuckQueueAlertThresholdSeconds: number;
  autoRepairEnabled: boolean;
  autoRepairIntervalSeconds: number;
  autoRepairMaxDocuments: number;
  autoRepairIncludeFailedDocuments: boolean;
  autoRepairIncludeUploadedDocuments: boolean;
  autoRepairMinDocumentAgeMinutes: number;
}

export interface KnowledgeBaseAttachmentSettings {
  enabled: boolean;
  maxAttachmentsPerDocument: number;
  maxAttachmentBytes: number;
  maxCaptionLength: number;
  maxAltTextLength: number;
  allowedImageMimeTypesCsv: string;
}

export interface KnowledgeBaseSettings {
  retrieval: KnowledgeBaseRetrievalSettings;
  ingestion: KnowledgeBaseIngestionSettings;
  attachments: KnowledgeBaseAttachmentSettings;
}

export interface KnowledgeBaseTenantSettingsResponse {
  effective: KnowledgeBaseSettings;
  overrides: Record<string, string>;
}

export interface KnowledgeBaseHealth {
  generatedAt: string;
  documentsTotal: number;
  documentsReady: number;
  documentsFailed: number;
  chunksTotal: number;
  embeddedChunks: number;
  jobsQueued: number;
  jobsProcessing: number;
  jobsFailed: number;
  jobsDeadLetter: number;
  oldestQueuedJobCreatedAt?: string | null;
  oldestQueuedJobAgeSeconds?: number | null;
  isIngestionStuck: boolean;
  stuckQueueAlertThresholdSeconds: number;
  semanticScoreThreshold: number;
  chunkMaxChars: number;
  chunkOverlapChars: number;
  retryMaxAttempts: number;
  retryBackoffBaseSeconds: number;
  retryBackoffMaxSeconds: number;
}

export interface KnowledgeRetrievalMetrics {
  windowStart: string;
  windowEnd: string;
  totalRequests: number;
  sampledRequests: number;
  emptyResultCount: number;
  emptyResultRate: number;
  p50LatencyMilliseconds: number;
  p95LatencyMilliseconds: number;
  semanticRequests: number;
  keywordRequests: number;
  hybridRequests: number;
}

export interface UpdateKnowledgeBaseSettingsRequest {
  retrievalSemanticScoreThreshold?: number | null;
  retrievalDefaultMaxChunks?: number | null;
  retrievalMaxAllowedChunks?: number | null;
  retrievalQueryMessageCount?: number | null;
  retrievalEmbeddingQueryMaxChars?: number | null;
  retrievalKeywordQueryMaxChars?: number | null;
  ingestionChunkMaxChars?: number | null;
  ingestionChunkOverlapChars?: number | null;
  ingestionPollIntervalSeconds?: number | null;
  ingestionRetryMaxAttempts?: number | null;
  ingestionRetryBackoffBaseSeconds?: number | null;
  ingestionRetryBackoffMaxSeconds?: number | null;
  ingestionStuckQueueAlertThresholdSeconds?: number | null;
  ingestionAutoRepairEnabled?: boolean | null;
  ingestionAutoRepairIntervalSeconds?: number | null;
  ingestionAutoRepairMaxDocuments?: number | null;
  ingestionAutoRepairIncludeFailedDocuments?: boolean | null;
  ingestionAutoRepairIncludeUploadedDocuments?: boolean | null;
  ingestionAutoRepairMinDocumentAgeMinutes?: number | null;
  attachmentsEnabled?: boolean | null;
  attachmentsMaxAttachmentsPerDocument?: number | null;
  attachmentsMaxAttachmentBytes?: number | null;
  attachmentsMaxCaptionLength?: number | null;
  attachmentsMaxAltTextLength?: number | null;
  attachmentsAllowedImageMimeTypesCsv?: string | null;
}